import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router";

import { Box, Grid, Alert, Button } from "@mui/material";

import api from "../../../common/utils/api";
import { FOOTER_HEIGHT } from "../../common/utils/constants";
import HubCard from "../../../common/components/HubCard";
import LoadingFallback from "../../../common/components/LoadingFallback";
import { getBadResponseErrorMessage } from "../../../common/utils/helpers";
import { handleWidgetClose } from "../../common/utils/helpers";

function EmailGen() {
  const { projectId, emailId } = useParams();
  const [searchParams] = useSearchParams();

  const callbackUrl = searchParams.get("callback");
  const collectionItemId = searchParams.get("collectionItemId");
  const footerHeight = FOOTER_HEIGHT;

  const [postProcessing, setPostProcessing] = useState(false);
  const [loadingEmailGeneration, setLoadingEmailGeneration] = useState(false);
  const [loadingEmailProcessing, setLoadingEmailProcessing] = useState(false);
  const [generationError, setGenerationError] = useState("");
  const [generationSuccessStatement, setGenerationSuccessStatement] = useState("");
  const [processingSuccessStatement, setProcessingSuccessStatement] = useState("");
  const [generationComplete, setGenerationComplete] = useState(false);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [processingFailed, setProcessingFailed] = useState(false);
  const [processingError, setProcessingError] = useState("");
  const [warnings, setWarnings] = useState(null);

  const emailGenerationHandler = useCallback(async () => {
    setLoadingEmailGeneration(true);
    const payload = Object.fromEntries(new URLSearchParams(window.location.search));

    try {
      const response = await api.post(`/email-gen/${projectId}/generate/${emailId}`, payload);
      const responseError = response?.data?.error;

      if (responseError) {
        setGenerationError(responseError);
      } else {
        setGenerationComplete(true);
        setGenerationSuccessStatement(response?.data?.generationStatement ?? "");
        setPostProcessing(response?.data?.postProcess ?? false);
        setWarnings(response?.data?.warnings ?? null);
        const byteCharacters = atob(response?.data?.file?.fileContents);
        const byteArray = new Uint8Array(Array.from(byteCharacters, (char) => char.charCodeAt(0)));

        const blob = new Blob([byteArray]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "email.eml";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.log(err);
      const errorMessage = getBadResponseErrorMessage(err);
      setGenerationError(errorMessage);
    } finally {
      setLoadingEmailGeneration(false);
    }
  }, [projectId, emailId]);

  const emailProcessingHandler = async () => {
    setLoadingEmailProcessing(true);
    const payload = Object.fromEntries(new URLSearchParams(window.location.search));
    try {
      const response = await api.post(`/email-gen/${projectId}/post-process/${emailId}`, payload);
      const responseError = response?.data?.error;

      if (responseError) {
        setProcessingFailed(true);
        setProcessingError(responseError);
      } else {
        setProcessingComplete(true);
        setProcessingSuccessStatement(response?.data?.successStatement ?? "");
      }
    } catch (err) {
      setProcessingFailed(true);
      setProcessingError(getBadResponseErrorMessage(err));
    } finally {
      setLoadingEmailProcessing(false);
    }
  };

  const closeHandler = () => {
    if (callbackUrl) {
      handleWidgetClose({ callbackUrl: callbackUrl, projectId });
    } else if (collectionItemId) {
      handleWidgetClose({ collectionItemId: collectionItemId });
    } else {
      window.close();
    }
  };

  const completePostProcessing = () => {
    setPostProcessing(false);
  };

  useEffect(() => {
    if (!generationComplete && !generationError) {
      emailGenerationHandler();
    }
  }, [generationError, generationComplete, emailGenerationHandler]);

  const PostProcessing = () => {
    return (
      <Grid size={{ xs: 12 }}>
        {generationSuccessStatement !== "" && !processingComplete && !processingFailed && (
          <Alert variant="outlined" severity={postProcessing ? "info" : "success"}>
            {generationSuccessStatement}
            <Grid container spacing={1} sx={{ mt: 1 }} width="100%">
              <Grid>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={loadingEmailProcessing}
                  onClick={emailProcessingHandler}
                >
                  Yes
                </Button>
              </Grid>
              <Grid>
                {/* <Button variant="outlined" color="primary" disabled={loadingEmailProcessing} onClick={closeHandler}> */}
                <Button
                  variant="outlined"
                  color="primary"
                  disabled={loadingEmailProcessing}
                  onClick={completePostProcessing}
                >
                  No
                </Button>
              </Grid>
            </Grid>
          </Alert>
        )}

        {loadingEmailProcessing && <LoadingFallback message="Processing" containerSx={{ height: "100%" }} />}

        {processingSuccessStatement !== "" && <Alert severity="success">{processingSuccessStatement}</Alert>}

        {processingFailed && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {`Post-processing failed. ${processingError}. Please close this window.`}
          </Alert>
        )}
      </Grid>
    );
  };

  return (
    <Box height={`calc(100vh - ${footerHeight} - 50%)`} width="calc(100vw - 50%)">
      <HubCard
        title="Email Generation"
        actions={{
          buttons: [
            {
              title: "Close",
              disabled:
                loadingEmailGeneration ||
                loadingEmailProcessing ||
                (postProcessing && !processingComplete && !processingFailed),
              onClick: closeHandler,
            },
          ],
        }}
      >
        {loadingEmailGeneration && <LoadingFallback message="Generating Email" containerSx={{ height: "100%" }} />}

        {!loadingEmailGeneration && (
          <Grid container spacing={1} overflow="auto" width="100%">
            {!processingFailed && (
              <>
                {generationError && (
                  <Grid size={{ xs: 12 }}>
                    <Alert severity="error">{generationError}</Alert>
                  </Grid>
                )}
                {generationComplete && !loadingEmailProcessing && !processingComplete && (
                  <>
                    <Grid size={{ xs: 12 }}>
                      <Alert severity="success">
                        Email Generation Has Been Completed. You Can Now Send Your EML file.
                      </Alert>
                    </Grid>
                  </>
                )}
                {warnings !== null &&
                  warnings.map((warning, index) => (
                    <Grid size={{ xs: 12 }} key={index}>
                      <Alert severity="warning">{warning}</Alert>
                    </Grid>
                  ))}
              </>
            )}
            {postProcessing && <PostProcessing />}
          </Grid>
        )}
      </HubCard>
    </Box>
  );
}

export default EmailGen;
